import React, { useState, useEffect } from 'react';
import UserLayout from '../../components/layouts/UserLayout';
import { useAuth } from '../../context/AuthContext';
import { ticketAPI, publicAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Calendar, MapPin, Ticket, AlertTriangle, Download, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { formatPrice, formatDateTime } from '../../lib/utils';

const MemberTickets = () => {
  const { isMember } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [cities, setCities] = useState([]);
  const [monuments, setMonuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedMonument, setSelectedMonument] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    loadCitiesAndMonuments();
  }, []);

  useEffect(() => {
    loadTickets();
  }, [selectedCity, selectedMonument, selectedDate]);

  const loadCitiesAndMonuments = async () => {
    try {
      const [citiesRes, monumentsRes] = await Promise.all([
        publicAPI.listCities(),
        publicAPI.listMonuments(),
      ]);
      setCities(citiesRes.data);
      setMonuments(monumentsRes.data);
    } catch (error) {
      console.error('Failed to load cities/monuments:', error);
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCity !== 'all') params.city_id = selectedCity;
      if (selectedMonument !== 'all') params.monument_id = selectedMonument;
      if (selectedDate) params.date = selectedDate;
      
      const response = await ticketAPI.search(params);
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadClick = (slot) => {
    setSelectedSlot(slot);
    setQuantity(1); // Reset quantity when opening dialog
    setConfirmDialog(true);
  };

  const handleQuantityChange = (delta) => {
    if (!selectedSlot) return;
    const maxQty = selectedSlot.remaining || 1;
    const newQty = Math.max(1, Math.min(maxQty, quantity + delta));
    setQuantity(newQty);
  };

  const handleConfirmDownload = async () => {
    if (!selectedSlot) return;
    
    setDownloading(true);
    try {
      // Members can download multiple, External users always get 1
      const downloadQty = isMember ? quantity : 1;
      const response = await ticketAPI.download(selectedSlot.id, downloadQty);
      toast.success(response.data.message || 'Ticket(s) downloaded successfully!');
      
      // Download files properly using fetch and blob
      if (response.data.download_links && response.data.download_links.length > 0) {
        for (let i = 0; i < response.data.download_links.length; i++) {
          const link = response.data.download_links[i];
          try {
            const url = ticketAPI.getFile(link.token);
            const fileResponse = await fetch(url);
            
            if (!fileResponse.ok) {
              toast.error(`Failed to download ticket ${i + 1}`);
              continue;
            }
            
            const blob = await fileResponse.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `ticket_${selectedSlot.monument_name || 'download'}_${i + 1}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
            
            // Small delay between downloads
            if (i < response.data.download_links.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (err) {
            console.error('Download error:', err);
            toast.error(`Failed to download ticket ${i + 1}`);
          }
        }
      }
      
      setConfirmDialog(false);
      setQuantity(1);
      loadTickets(); // Refresh availability
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download ticket');
    } finally {
      setDownloading(false);
    }
  };

  const filteredMonuments = selectedCity !== 'all' 
    ? monuments.filter(m => m.city_id === selectedCity)
    : monuments;

  const getUserPrice = (slot) => {
    const pricing = slot.pricing || {};
    return isMember ? pricing.member : pricing.external;
  };

  return (
    <UserLayout title="Browse Tickets" subtitle="Search and download available tickets">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={selectedCity} onValueChange={(v) => { setSelectedCity(v); setSelectedMonument('all'); }}>
          <SelectTrigger className="w-48" data-testid="filter-city">
            <SelectValue placeholder="All Cities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map(city => (
              <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={selectedMonument} onValueChange={setSelectedMonument}>
          <SelectTrigger className="w-48" data-testid="filter-monument">
            <SelectValue placeholder="All Monuments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Monuments</SelectItem>
            {filteredMonuments.map(m => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-48"
          data-testid="filter-date"
        />
        
        {(selectedCity !== 'all' || selectedMonument !== 'all' || selectedDate) && (
          <Button 
            variant="ghost" 
            onClick={() => { setSelectedCity('all'); setSelectedMonument('all'); setSelectedDate(''); }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Tickets Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12">
          <Ticket className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-900">No tickets available</h3>
          <p className="text-slate-500 mt-1">Try adjusting your filters or check back later</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tickets.map((slot) => {
            const price = getUserPrice(slot);
            const isAvailable = isMember ? slot.remaining > 0 : slot.available;
            
            return (
              <Card key={slot.id} className="ticket-card card-hover overflow-hidden" data-testid={`ticket-card-${slot.id}`}>
                {/* Image */}
                {slot.monument_image ? (
                  <div className="h-40 relative">
                    <img 
                      src={slot.monument_image} 
                      alt={slot.monument_name}
                      className="w-full h-full object-cover"
                    />
                    <div className="monument-image-overlay absolute inset-0" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <h3 className="font-semibold text-white">{slot.monument_name}</h3>
                    </div>
                  </div>
                ) : (
                  <div className="h-32 bg-slate-100 flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-slate-300" />
                  </div>
                )}
                
                <CardContent className="p-4">
                  {!slot.monument_image && (
                    <h3 className="font-semibold text-slate-900 mb-2">{slot.monument_name}</h3>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <Calendar className="w-4 h-4" />
                    {formatDateTime(slot.slot_start_at)}
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="outline" className="capitalize">{slot.category_label}</Badge>
                    {isMember ? (
                      <span className="text-sm font-medium">
                        {slot.remaining > 0 ? (
                          <span className="text-emerald-600">{slot.remaining} remaining</span>
                        ) : (
                          <span className="text-red-600">Sold out</span>
                        )}
                      </span>
                    ) : (
                      <Badge className={isAvailable ? 'badge-success' : 'badge-error'}>
                        {isAvailable ? 'Available' : 'Sold out'}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-slate-900">
                      {price ? formatPrice(price) : 'N/A'}
                    </span>
                    <Button
                      className="bg-indigo-600 hover:bg-indigo-700"
                      disabled={!isAvailable || !price}
                      onClick={() => handleDownloadClick(slot)}
                      data-testid={`download-btn-${slot.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Download</DialogTitle>
            <DialogDescription>
              You are about to download {isMember ? 'ticket(s)' : 'a ticket'}. Please review the details below.
            </DialogDescription>
          </DialogHeader>
          
          {selectedSlot && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold">{selectedSlot.monument_name}</h4>
                <p className="text-sm text-slate-500">{formatDateTime(selectedSlot.slot_start_at)}</p>
                <p className="text-sm text-slate-500 capitalize">{selectedSlot.category_label}</p>
                <p className="text-lg font-medium mt-2">
                  Price per ticket: {formatPrice(getUserPrice(selectedSlot))}
                </p>
              </div>
              
              {/* Quantity Selector - Only for Members */}
              {isMember && selectedSlot.remaining > 1 && (
                <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <Label className="text-sm font-medium text-indigo-800 mb-2 block">
                    Select Quantity (Max: {selectedSlot.remaining})
                  </Label>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      data-testid="qty-decrease-btn"
                      className="h-10 w-10"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      min={1}
                      max={selectedSlot.remaining}
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        setQuantity(Math.max(1, Math.min(selectedSlot.remaining, val)));
                      }}
                      className="w-20 text-center text-lg font-semibold"
                      data-testid="qty-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= selectedSlot.remaining}
                      data-testid="qty-increase-btn"
                      className="h-10 w-10"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Total Price Summary */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-emerald-800">
                    Total ({isMember ? quantity : 1} ticket{(isMember ? quantity : 1) > 1 ? 's' : ''})
                  </span>
                  <span className="text-2xl font-bold text-emerald-700">
                    {formatPrice(getUserPrice(selectedSlot) * (isMember ? quantity : 1))}
                  </span>
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">No Refunds Policy</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      All downloads are final. Once you download, tickets cannot be refunded or exchanged.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleConfirmDownload}
              disabled={downloading}
              data-testid="confirm-download-btn"
            >
              {downloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download {isMember && quantity > 1 ? `${quantity} Tickets` : 'Ticket'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UserLayout>
  );
};

export default MemberTickets;
